import {
	CheckCircle,
	CurrencyCircleDollar,
	CaretDown,
	CaretUp,
} from '@phosphor-icons/react';
import Text from '../../../components/atoms/Text';
import Heading from '../../../components/atoms/Heading';
import PrimaryButton from '../../../components/atoms/Button';
import { putUserDeclarationStatus } from '../../../services/flowApproval.service';
import { useEffect, useState } from 'react';
import { Detail } from '../../../@types/Declarations.type';
import Modal from '../../../components/molecules/Modal/Modal';
import ROUTES from '../../../shared/constants/routes';
import { useNavigate } from 'react-router-dom';
import { ALERT_TYPE } from '../../../components/molecules/Alert';
import { showAlert } from '../../../redux/slices/alert.slice';
import { useDispatch } from 'react-redux';
import { EVENT_TYPES } from '../../../shared/constants/events';
import { ANALYTICS_EVENTS } from '../../../shared/constants/events';
import { sendEvent } from '../../../services/user-interaction.service';
import useClarityEvent from '../../../hooks/useClarityEvent';
interface Step3Props {
	id?: number;
	amount: number;
	precalculate_details: Detail;
	viewApprove?: boolean;
	viewModal?: (value: boolean) => void;
	user_id?: number;
}

const Step3 = ({
	id,
	amount,
	precalculate_details,
	viewApprove = true,
	viewModal,
	user_id,
}: Step3Props) => {
	const navigate = useNavigate();
	const { trackEvent } = useClarityEvent();
	const [viewDetail, setViewDetail] = useState(false);
	const [viewApproveModal, setViewApproveModal] = useState(false);
	const dispatch = useDispatch();
	const handleApproveDeclaration = () => {
		if (id) {
			putDeclarationStatus(Number(id));
			approvedDeclaration();
		}
	};

	const putDeclarationStatus = async (id: number) => {
		try {
			const response = await putUserDeclarationStatus(id, {
				status: 'approved',
			});
			if (response?.data?.resource?.status === 'approved') {
				setViewApproveModal(true);
				setTimeout(() => {
					onCloseApproveModal();
				}, 2000);
			}
		} catch (error) {
			const alert = {
				type: ALERT_TYPE.ERROR,
				title: 'Error',
				description: 'Error al aprobar la declaración',
			};
			dispatch(showAlert(alert));
		}
	};

	const approvedDeclaration = async () => {
		if (!user_id) return;
		try {
			await sendEvent({
				user_id: user_id,
				event: ANALYTICS_EVENTS.APPROVAL_DECLARATIONS,
				source: 'web',
				event_payload: {
					subsection: EVENT_TYPES.APPROVAL_DECLARATIONS_APPROVED_DECLARATION,
				},
			});
			trackEvent(EVENT_TYPES.APPROVAL_DECLARATIONS_APPROVED_DECLARATION);
		} catch (error) {}
	};

	const viewDetailCalculation = async () => {
		if (!user_id) return;
		try {
			await sendEvent({
				user_id: user_id,
				event: ANALYTICS_EVENTS.APPROVAL_DECLARATIONS,
				source: 'web',
				event_payload: {
					subsection: EVENT_TYPES.APPROVAL_DECLARATIONS_VIEW_DETAIL_CALCULATION,
				},
			});
			trackEvent(EVENT_TYPES.APPROVAL_DECLARATIONS_VIEW_DETAIL_CALCULATION);
		} catch (error) {}
	};

	const sumActivePlatformValues = (data: any[]): number => {
		return data?.reduce((total, item) => {
			if (!item.active) return total;
			if ('platform' in item) {
				return total + (item.platform || 0);
			}
			const nestedSum = Object.values(item).reduce(
				(sum: number, value: any) => {
					if (value && typeof value === 'object' && 'platform' in value) {
						return sum + (value.platform || 0);
					}
					return sum;
				},
				0,
			);

			return total + nestedSum;
		}, 0);
	};

	const result = sumActivePlatformValues(
		precalculate_details?.additionalInformation?.ISR,
	);

	const sumActiveTaxRetained = (data: any[]): number => {
		return data?.reduce((total, item) => {
			if (!item.active) return total;
			if ('tax_retained' in item) {
				return total + (item.tax_retained || 0);
			}

			return total;
		}, 0);
	};

	const taxRetained = sumActiveTaxRetained(
		precalculate_details?.additionalInformation?.ISR,
	);

	const onCloseApproveModal = () => {
		setViewApproveModal(false);
		navigate(ROUTES.TAXES);
	};

	useEffect(() => {
		if (viewModal) viewModal(viewApproveModal);
	}, [viewApproveModal]);

	return (
		<>
			{viewApprove && (
				<>
					<div className='self-stretch inline-flex justify-start items-center gap-2.5 mt-4'>
						<CurrencyCircleDollar
							weight='duotone'
							color='var(--h-primary)'
							size={16}
						/>
						<Text
							size='body-3'
							weight='medium'
						>
							Resultado y resumen
						</Text>
					</div>
					<div className='self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-[#e5e5e5]' />
				</>
			)}
			<div className='flex flex-col justify-center items-center gap-2 w-full'>
				<Text
					size='body-2'
					weight='regular'
				>
					Resultado final
				</Text>
				<Heading
					size='l'
					weight='medium'
				>
					$
					{Math.abs(amount).toLocaleString('es-MX', {
						maximumFractionDigits: 0,
						minimumFractionDigits: 0,
					})}
				</Heading>
				<Text
					size='body-3'
					weight='light'
				>
					{amount === 0 ? 'en cero' : amount > 0 ? 'a favor' : 'a pagar'}
				</Text>
			</div>

			<div
				className='flex justify-center items-center w-full cursor-pointer'
				onClick={() => {
					if (!viewDetail) {
						viewDetailCalculation();
					}
					setViewDetail(!viewDetail);
				}}
			>
				{viewDetail ? (
					<div className='flex justify-center items-center gap-2'>
						<Text
							size='body-3'
							weight='regular'
							color='primary'
						>
							Ocultar
						</Text>
						<CaretUp
							size={16}
							color='var(--h-primary)'
						/>
					</div>
				) : (
					<div className='flex justify-center items-center gap-2'>
						<Text
							size='body-3'
							weight='regular'
							color='primary'
						>
							Detalle
						</Text>
						<CaretDown
							size={16}
							color='var(--h-primary)'
						/>
					</div>
				)}
			</div>
			{viewApprove && (
				<PrimaryButton
					onClick={handleApproveDeclaration}
					label='Aprobar declaración'
					variant='primary'
					icon={<CheckCircle size={24} />}
				/>
			)}
			{viewDetail && (
				<>
					<div className='w-full px-2 pb-2 bg-h-primary-100 rounded-lg outline outline-1 outline-offset-[-1px] outline-h-neutral-90 inline-flex flex-col justify-start items-start gap-2'>
						<div className='px-2 py-1 bg-h-neutral-90 rounded-bl-lg rounded-br-lg inline-flex justify-start items-center gap-[3px]'>
							<Text
								size='mini-1'
								weight='regular'
							>
								ISR
							</Text>
						</div>
						<div className='self-stretch flex flex-col justify-start items-start gap-1'>
							<div className='w-full pl-2 flex justify-between items-center'>
								<Text
									size='mini-1'
									weight='light'
								>
									Ingreso
								</Text>
								<Text
									size='mini-1'
									weight='light'
								>
									${Math.round(result).toLocaleString('es-MX')}
								</Text>
							</div>
							<div className='w-full pl-2 mt-2 flex justify-between items-center'>
								<Text
									size='mini-1'
									weight='light'
								>
									ISR a pagar
								</Text>
								<Text
									size='mini-1'
									weight='light'
								>
									${Math.round(taxRetained).toLocaleString('es-MX')}
								</Text>
							</div>
							<div className='w-full pl-2 flex justify-between items-center'>
								<Text
									size='mini-1'
									weight='light'
								>
									- Retenciones ISR
								</Text>
								<Text
									size='mini-1'
									weight='light'
								>
									${Math.round(taxRetained).toLocaleString('es-MX')}
								</Text>
							</div>
						</div>
						<div className='self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-h-neutral-90' />
						<div className='w-full flex justify-between items-center'>
							<Text
								size='mini-1'
								weight='light'
							>
								= Total ISR
							</Text>
							<Text
								size='mini-1'
								weight='light'
							>
								${Math.round(0).toLocaleString('es-MX')}
							</Text>
						</div>
					</div>
					<div className='self-stretch px-2 pb-2 bg-h-primary-100 rounded-lg outline outline-1 outline-offset-[-1px] outline-h-neutral-90 inline-flex flex-col justify-start items-start gap-2'>
						<div className='px-2 py-1 bg-h-neutral-90 rounded-bl-lg rounded-br-lg inline-flex justify-start items-center gap-[3px]'>
							<Text
								size='mini-1'
								weight='regular'
							>
								IVA
							</Text>
						</div>
						<div className='self-stretch flex flex-col justify-start items-start gap-1'>
							<div className='flex justify-between items-center w-full'>
								<Text
									size='mini-1'
									weight='light'
								>
									Ingreso
								</Text>
								<Text
									size='mini-1'
									weight='light'
								>
									$
									{Math.round(
										precalculate_details.additionalInformation.IVA.platform,
									).toLocaleString('es-MX')}
								</Text>
							</div>
							<div className='flex pl-2 mt-2 justify-between items-center w-full'>
								<Text
									size='mini-1'
									weight='light'
								>
									IVA a pagar
								</Text>
								<Text
									size='mini-1'
									weight='light'
								>
									$
									{precalculate_details.additionalInformation.paymentMethod ===
									'PROVISIONAL'
										? Math.round(
												precalculate_details.additionalInformation.IVA
													.tax_retained * 2,
											).toLocaleString('es-MX')
										: Math.round(
												precalculate_details.additionalInformation.IVA
													.tax_retained,
											).toLocaleString('es-MX')}
								</Text>
							</div>
							<div className='flex pl-2 justify-between items-center w-full'>
								<Text
									size='mini-1'
									weight='light'
								>
									- Retenciones IVA
								</Text>
								<Text
									size='mini-1'
									weight='light'
								>
									$
									{Math.round(
										precalculate_details.additionalInformation.IVA.tax_retained,
									).toLocaleString('es-MX')}
								</Text>
							</div>

							<div className='flex pl-2 justify-between items-center w-full'>
								<Text
									size='mini-1'
									weight='light'
								>
									- Deducciones
								</Text>
								<Text
									size='mini-1'
									weight='light'
								>
									$
									{Math.round(
										precalculate_details.additionalInformation.IVA.deductions,
									).toLocaleString('es-MX')}
								</Text>
							</div>
						</div>
						<div className='self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-h-neutral-90' />
						<div className='flex justify-between items-center w-full'>
							<Text
								size='mini-1'
								weight='light'
							>
								= Total IVA
							</Text>
							<Text
								size='mini-1'
								weight='light'
							>
								$
								{Math.round(
									Math.abs(precalculate_details.totalBalance),
								).toLocaleString('es-MX')}
							</Text>
						</div>
					</div>
					<div className='flex justify-between items-center w-full'>
						<Text
							size='caption'
							weight='medium'
						>
							Total:
						</Text>
						<Text
							size='caption'
							weight='medium'
						>
							${' '}
							{Math.round(
								Math.abs(precalculate_details.totalBalance),
							).toLocaleString('es-MX')}
						</Text>
					</div>
				</>
			)}
			<Modal
				sx='w-full max-w-[360px]'
				open={viewApproveModal}
				onClose={onCloseApproveModal}
			>
				<div className='flex flex-col items-center justify-center gap-6 p-4'>
					<CheckCircle
						color='var(--h-success-50)'
						size={60}
						weight='duotone'
					/>
					<Text
						size='body-1'
						weight='medium'
						sx='text-center'
					>
						Declaración aprobada
					</Text>
					<Text
						size='body-3'
						weight='light'
						sx='text-center'
					>
						Gracias por aprobar el precálculo de tu declaración. Te avisaremos
						cuando la hayamos presentado ante el SAT.
					</Text>
				</div>
			</Modal>
		</>
	);
};

export default Step3;
